import { useIntl } from 'react-intl'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Caption from 'LuxKit/Typography/Caption'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import MarkdownRender from 'LuxKit/MarkdownRender'

const CancellationReasonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${rem(24)};
  & > * {
    margin-bottom: ${rem(4)};
    &:last-child {
      margin-bottom: 0;
    }
  }

  ${mediaQueryUp.tablet} {
    display: block;
    gap: ${rem(20)};
    max-height: ${rem(248)};
    max-width: ${rem(202)};
    column-count: auto;
    column-width: ${rem(200)};
    column-gap: ${rem(20)};
    align-self: stretch;
    margin-bottom: ${rem(16)};
  }

`

const DisclaimerCaption = styled(Caption)`
  max-width: ${rem(500)};
`

interface Props {
  quote?: App.InsuranceQuote
}

function CFMRUnforseenEventsModalContent(props: Props) {
  const { quote } = props
  const intl = useIntl()

  const cancellationReasons = [
    intl.formatMessage({
      id: 'CmuMC_8lXtNBFn8H3_bz',
      defaultMessage: 'Illness/injury (including Covid)',
      description: 'Insurance cancellation reason - illness or injury including Covid',
    }),
    intl.formatMessage({
      id: 'z7ATMEBi6aQZq21S1iBE',
      defaultMessage: 'Pre-existing medical condition',
      description: 'Insurance cancellation reason - pre-existing medical condition',
    }),
    intl.formatMessage({
      id: 'hknq2PDavHBTp-MRKQhm',
      defaultMessage: 'Court summons',
      description: 'Insurance cancellation reason - court summons in the insurance section',
    }),
    intl.formatMessage({
      id: 'YmhmDKb82H_jvk5flM8F',
      defaultMessage: 'Workplace redundancy',
      description: 'Insurance cancellation reason - workplace redundancy',
    }),
    intl.formatMessage({
      id: 'd72VnaLasa_yAYi6FmA4',
      defaultMessage: 'Death of immediate family',
      description: 'Insurance cancellation reason - death of immediate family member',
    }),
    intl.formatMessage({
      id: 'CfvpLiNvhbLAJLfXYuOO',
      defaultMessage: 'Public transport failure',
      description: 'Insurance cancellation reason - public transport failure',
    }),
    intl.formatMessage({
      id: 'psmosg6tV3H_DpDqqkrn',
      defaultMessage: 'Relocated for work',
      description: 'Insurance cancellation reason - relocated for work',
    }),
    intl.formatMessage({
      id: 'eUVpwcGTewr3u59WOEij',
      defaultMessage: 'Armed forces & emergency services recall',
      description: 'Insurance cancellation reason - armed forces or emergency services recall',
    }),
    intl.formatMessage({
      id: 'NUw6k6D__WZhLV9TEdf6',
      defaultMessage: 'Home emergency',
      description: 'Insurance cancellation reason - home emergency in the insurance section',
    }),
    intl.formatMessage({
      id: 'pvOwjYE7azgExYv2oj8m',
      defaultMessage: 'Theft of documents',
      description: 'Insurance cancellation reason - theft of documents',
    }),
    intl.formatMessage({
      id: '7YfpPdgmXc5HKLkNo2L_',
      defaultMessage: 'Pregnancy complication',
      description: 'Insurance cancellation reason - pregnancy complication',
    }),
    intl.formatMessage({
      id: 'qKWfAs0EIDUO7O391rJ8',
      defaultMessage: 'Jury service',
      description: 'Insurance cancellation reason - jury service in the insurance flow',
    }),
    intl.formatMessage({
      id: 'yQskGqp_tzgkJxJF4zf1',
      defaultMessage: 'Flight disruption',
      description: 'Insurance cancellation reason - flight disruption in the insurance section',
    }),
    intl.formatMessage({
      id: 'QULSoEKCsiivTat14Zh5',
      defaultMessage: 'Mechanical breakdown',
      description: 'Insurance cancellation reason - mechanical breakdown',
    }),
    intl.formatMessage({
      id: 'tXJMLw5rta4hhzcwEM0I',
      defaultMessage: 'Adverse weather',
      description: 'Insurance cancellation reason - adverse weather in the insurance section',
    }),
    intl.formatMessage({
      id: 'rLtWk8G1BkiX0CqpqERq',
      defaultMessage: 'Changes to examination dates',
      description: 'Insurance cancellation reason - changes to examination dates',
    }),
  ]

  return (
    <Group direction="vertical">
      <CancellationReasonsContainer>
        {cancellationReasons.map((reason: string, index: number) => (
          <BodyTextBlock variant="medium" key={index} startIcon={<LineCheckIcon colour="highlight-primary-dark" />}>
            {reason}
          </BodyTextBlock>
        ))}
      </CancellationReasonsContainer>
      <DisclaimerCaption variant="medium" colour="neutral-one">
        <MarkdownRender content={quote?.disclaimer ?? ''} fontSize="small" />
      </DisclaimerCaption>
    </Group>
  )
}

export default CFMRUnforseenEventsModalContent
